import React from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  ListGroup,
  Button,
  Accordion,
  Alert,
} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../auth/auth-context';
import QuickStart from '../components/quick-start';
import {ImportFromPlatformsModal} from './import-pdf';

function HelpPage(): React.JSX.Element {
  const navigate = useNavigate();
  const {isAuthenticated} = useAuth();
  const [showImportFromPlatformsModal, setShowImportFromPlatformsModal] =
    React.useState(false);

  return (
    <Container className='my-5'>
      <h1>Help & Documentation</h1>
      <p className='lead'>
        Find tips, quickstart steps, and answers to common questions about using
        the application.
      </p>

      <Row className='mb-4'>
        <Col md={6}>
          <Card className='h-100'>
            <Card.Body>
              <Card.Title>Quick start</Card.Title>
              <QuickStart
                isAuthenticated={isAuthenticated}
                onOpenImportPlatformsModal={() => {
                  setShowImportFromPlatformsModal(true);
                }}
              />
            </Card.Body>
            <Card.Footer>
              <Button
                variant='outline-secondary'
                size='sm'
                onClick={() => {
                  void navigate('/jobs');
                }}
              >
                Jobs
              </Button>{' '}
              <Button
                variant='outline-secondary'
                size='sm'
                onClick={() => {
                  void navigate('/resumes');
                }}
              >
                Resumes
              </Button>{' '}
            </Card.Footer>
          </Card>
        </Col>

        <Col md={6}>
          <Card className='h-100'>
            <Card.Body>
              <Card.Title>Common tasks</Card.Title>
              <ListGroup variant='flush'>
                <ListGroup.Item>
                  Import a resume (JSON or supported formats) and edit fields in
                  the Resume editor.
                </ListGroup.Item>
                <ListGroup.Item>
                  Paste a job posting into a Job entry and save — the AI
                  extractor will parse responsibilities & skills.
                </ListGroup.Item>
                <ListGroup.Item>
                  Generate an AI-tailored resume from a job + resume pair via
                  the "Generate" action on a Job or Resume.
                </ListGroup.Item>
                <ListGroup.Item>
                  Export to PDF from the resume preview page.
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
            <Card.Footer>
              {isAuthenticated ? (
                <Button
                  variant='success'
                  size='sm'
                  onClick={() => {
                    void navigate('/resumes');
                  }}
                >
                  Start now
                </Button>
              ) : (
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => {
                    void navigate('/signup');
                  }}
                >
                  Create an account
                </Button>
              )}
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <h4 className='mb-3'>Frequently asked questions</h4>
      <Accordion defaultActiveKey='0' className='mb-4'>
        <Accordion.Item eventKey='0'>
          <Accordion.Header>What does "AI tailoring" mean?</Accordion.Header>
          <Accordion.Body>
            The AI analyzes the job description and your resume to suggest
            changes that improve relevance: it highlights matching skills,
            suggests reordered bullets, and recommends keyword additions so your
            resume better aligns with the role.
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey='1'>
          <Accordion.Header>
            Is AI-generated content always correct?
          </Accordion.Header>
          <Accordion.Body>
            No — AI outputs may be incomplete or occasionally incorrect. Treat
            suggestions as a starting point and review/edit them for accuracy.
            The app shows a "Note" alert when using AI features describing this.
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey='2'>
          <Accordion.Header>How do I export a PDF?</Accordion.Header>
          <Accordion.Body>
            Open your resume in preview mode and choose "Export to PDF". If
            export fails, try reloading the page, and then retry. If it still
            fails, see the Troubleshooting section below.
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey='3'>
          <Accordion.Header>Account & authentication issues</Accordion.Header>
          <Accordion.Body>
            If you cannot log in, ensure your email is verified (check your
            inbox). If your account is disabled or banned, the app will show a
            message and restrict access — contact support if you believe this is
            an error.
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <h4 className='mb-3'>Troubleshooting</h4>
      <ListGroup className='mb-4'>
        <ListGroup.Item>
          API errors: check your network connection and retry. If the backend is
          unreachable, you may still edit locally.
        </ListGroup.Item>
        <ListGroup.Item>
          Login problems: use the "Forgot password" flow on the login page or
          request a password reset.
        </ListGroup.Item>
        <ListGroup.Item>
          Export problems: try a different browser or disable browser extensions
          that block downloads.
        </ListGroup.Item>
      </ListGroup>

      <h4 className='mb-3'>Still need help?</h4>
      <Alert variant='secondary'>
        Contact support:{' '}
        <a href='mailto:support@selfdrivingresume.com'>
          support@selfdrivingresume.com
        </a>
        <div className='mt-2'>
          For bugs, please include: a short description, steps to reproduce, the
          page you were on, and any console errors.
        </div>
      </Alert>
      <ImportFromPlatformsModal
        show={showImportFromPlatformsModal}
        onHide={() => {
          setShowImportFromPlatformsModal(false);
        }}
      />
    </Container>
  );
}

export default HelpPage;
